/**
 * M02 意图管理 shell · 三 tab：Intent 列表 / L2 YAML 编辑 / L1 prompt 维护
 *
 * 承接：T11 §3.2 路由 /intent = M02 · M02 README 四子页规划
 */
import { useState } from "react";

import { IntentList } from "@/components/m02/intent-list";
import { L1AbConfig } from "@/components/m02/l1-ab-config";
import { L1PromptTemplateEditor } from "@/components/m02/l1-prompt-template-editor";
import { L2YamlEditor } from "@/components/m02/l2-yaml-editor";
import { type RuleSurfaceRow, RuleSurfaceSummary } from "@/components/shared/rule-surface-summary";
import {
	SingleStageTestPanel,
	type SingleStageTestSample,
} from "@/components/shared/single-stage-test-panel";

type Tab = "list" | "l2" | "l1";

const TABS: ReadonlyArray<{ id: Tab; label: string; testid: string }> = [
	{ id: "list", label: "分类列表", testid: "m02-tab-list" },
	{ id: "l2", label: "规则编辑", testid: "m02-tab-l2" },
	{ id: "l1", label: "提示词维护", testid: "m02-tab-l1" },
];

const INTENT_TEST_SAMPLES: SingleStageTestSample[] = [
	{
		sampleId: "P10TA001",
		sampleMessage: "我要退款，但是忘记订单号了",
		expectedIntent: "refund_request",
		expectedPlaybook: "PB02_refund",
		scenario: "售后退款 / 缺订单信息 / L1 业务语义",
		verificationPoint: "退款诉求属于普通业务语义，当前应由 L1 V0.3 识别，再由方案追问订单信息。",
		assetName: "L1 V0.3 / 退款申请意图",
		boundary: "只判断客户意图，不直接判断退款是否可执行。",
	},
	{
		sampleId: "P11TA011",
		sampleMessage: "杯子到货碎了，但是我忘记订单号了",
		expectedIntent: "order_issue",
		expectedPlaybook: "PB03_return",
		scenario: "订单异常 / 状态缺口",
		verificationPoint:
			"到货破损应进入 order_issue；P1 RoutingTable 先路由到 PB03，branch 细分后续再做。",
		assetName: "L1 V0.3 / order_issue 识别合同",
		boundary: "只验证新增意图和路由承接，不生成售后处理承诺。",
	},
	{
		sampleId: "P10TA004",
		sampleMessage: "新手滑雪板和礼品卡我应该选哪个？",
		expectedIntent: "product_recommendation",
		expectedPlaybook: "PB06_pre_sale",
		scenario: "售前导购 / 推荐对比",
		verificationPoint: "推荐、对比类表达归入 PB06 售前导购域，不新拆淘宝同名方案。",
		assetName: "分类规则 / 售前导购域",
		boundary: "只判断推荐意图归属，不在这里判断推荐结果质量。",
	},
	{
		sampleId: "P10TA006",
		sampleMessage: "这个优惠码 SPRING10 还能用吗？",
		expectedIntent: "discount_inquiry",
		expectedPlaybook: "PB07_discount",
		scenario: "优惠咨询 / 优惠码可用性",
		verificationPoint:
			"优惠码、折扣规则要进入优惠咨询；当前不是 L2 快路径，不能再展示为优惠关键词规则命中。",
		assetName: "L1 V0.3 / 优惠咨询意图",
		boundary: "只判断优惠意图，不在这里承诺优惠码真实可用。",
	},
	{
		sampleId: "P10TA007",
		sampleMessage: "这单还没发货可以取消吗？",
		expectedIntent: "order_cancel",
		expectedPlaybook: "PB04_cancel",
		scenario: "售中取消 / 写操作保护",
		verificationPoint:
			"未带订单号的取消诉求进入 L1 业务判断；只有带明确订单号时才可能走 L2 快路径。",
		assetName: "L1 V0.3 / 取消订单意图",
		boundary: "只判断取消意图，不执行取消订单。",
	},
	{
		sampleId: "P10TA009",
		sampleMessage: "我地址写错了，能改到新地址吗？",
		expectedIntent: "address_change",
		expectedPlaybook: "PB05_address_change",
		scenario: "售中改地址 / 状态约束",
		verificationPoint: "改地址表达要进入地址修改，不被订单查询或物流跟踪误吸收。",
		assetName: "L1 V0.3 / 地址修改意图",
		boundary: "只判断改地址意图，不执行真实地址修改。",
	},
];

const INTENT_RULE_ROWS: RuleSurfaceRow[] = [
	{
		name: "分类列表",
		source: "P11 P0 13 个业务意图 + L2 快路径摘要",
		edit: "不在列表页直接改",
		effect: "只读说明",
		verify: "先看识别路径，再进入规则编辑或分类测试",
		tone: "readonly",
	},
	{
		name: "L2 快路径规则",
		source: "intent_rule.yaml",
		edit: "可编辑 YAML，保存时做 schema 校验",
		effect: "只处理转人工、带订单号取消、带订单号状态查询、问候感谢兜底",
		verify: "保存后先看快路径是否误吸普通业务语义",
		tone: "live",
	},
	{
		name: "L1 V0.3 业务识别",
		source: "L1 Prompt 模板 YAML + 商户级版本配置",
		edit: "可维护模板正文、版本和灰度比例",
		effect: "普通退款、退货、商品、优惠、订单异常等语义主路径",
		verify: "分类测试看 intent、服务阶段、规则命中是否符合真实链路",
		tone: "partial",
	},
];

export type M02View = "management" | "test";

export function M02Shell({ view = "management" }: { view?: M02View }) {
	const [tab, setTab] = useState<Tab>("list");

	if (view === "test") {
		return (
			<SingleStageTestPanel
				testId="intent-single-stage-test-panel"
				kind="intent"
				title="意图分类单环节测试"
				description="验证“客户在问什么”是否识别正确。P11 P0 下，普通业务语义走 L1 V0.3；L2 只用于少数明确快路径。"
				targetStage="intent_classification"
				stageLabel="意图分类"
				sampleId="P10TA001"
				sampleMessage="我要退款，但是忘记订单号了"
				expectedIntent="refund_request"
				expectedPlaybook="PB02_refund"
				assetName="L1 V0.3 / L2 快路径"
				boundary="当前优先验证分类结果、识别路径和置信边界，不直接判断最终回复好坏。"
				datasetHref="/test-datasets?mock=1#p10-single-stage-full-menu-coverage"
				samples={INTENT_TEST_SAMPLES}
			/>
		);
	}

	return (
		<div className="space-y-4" data-testid="m02-shell">
			<RuleSurfaceSummary
				rows={INTENT_RULE_ROWS}
				testId="intent-rule-surface-summary"
				description="这个页面只管“客户在问什么”。分类结果会影响处理路径匹配，但 L2 不再承担宽泛业务分类。"
			/>

			<nav className="flex gap-1 border-b border-surface-border">
				{TABS.map((t) => (
					<button
						type="button"
						key={t.id}
						data-testid={t.testid}
						className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
							tab === t.id
								? "border-brand-accent font-medium text-brand-accent"
								: "border-transparent text-text-secondary hover:text-text-primary"
						}`}
						onClick={() => setTab(t.id)}
					>
						{t.label}
					</button>
				))}
			</nav>

			<section className="pt-2">
				{tab === "list" ? <IntentList /> : null}
				{tab === "l2" ? <L2YamlEditor /> : null}
				{tab === "l1" ? (
					<div className="space-y-4">
						<L1PromptTemplateEditor />
						<L1AbConfig />
					</div>
				) : null}
			</section>
		</div>
	);
}
